<script setup>
import { useBreakpoints, useDataQueryStore } from '@daysmart/daysmart-vue-components/composables'
import { inferPagingFromApiResponse } from '~/core/api/helpers'

const { t } = useI18n()
const api = useApi()

const isLoading = ref(false)
const appointments = ref([])
const venueList = ref([])
const { current: currentBreakpoint } = useBreakpoints()
const isMultipleAppointmentPages = ref(false)

const maxFilterControlValue = computed(() => {
  return currentBreakpoint.value === 'xs' ? 1 : 3
})

async function fetchVenueFilterOptions() {
  isLoading.value = true
  const venueData = await api.listVenues()
  venueList.value = venueData.results
  isLoading.value = false
}

async function fetchAppointmentsOfferingTypes() {
  isLoading.value = true
  const appointmentsOfferingTypes = await api.listOfferingTypes({
    queryParameters: {
      schedule_type: ['appointment'],
      has_active_sessions: true,
    },
    expand: ['instructors', 'appointment_settings.default_venue', 'venue'],
  })

  if (!venueList.value)
    fetchVenueFilterOptions()

  appointments.value = appointmentsOfferingTypes.results
  isLoading.value = false
}

const instructorFiltersOptions = computed(() => {
  const instructorsMap = new Map()

  appointments.value?.forEach((offering) => {
    offering?.instructors?.forEach(({ id, name }) => {
      if (!instructorsMap.has(id))
        instructorsMap.set(id, { value: id, label: name })
    })
  })
  return Array.from(instructorsMap.values())
})

const appointmentTypesFilterOptions = computed(() => {
  const appointmentTypesMap = new Map()

  appointments.value?.forEach(({ id, name }) => {
    appointmentTypesMap.set(id, { value: id, label: name })
  })

  return Array.from(appointmentTypesMap.values())
})

const venueFilterOptions = computed(() => {
  const uniqueVenuesMap = new Map()

  for (const appointment of appointments.value || []) {
    const defaultVenue = appointment.appointment_settings?.default_venue

    if (defaultVenue && defaultVenue.id) {
      const venue = venueList.value?.find(({ id }) => id === defaultVenue.id)

      if (venue)
        uniqueVenuesMap.set(venue.id, { value: venue.id, label: venue.name })
    }
  }

  return Array.from(uniqueVenuesMap.values())
})

const dataQueryStore = useDataQueryStore('appointments', {
  enabled: true,
})

async function fetchContent({ state, setPagination }) {
  isLoading.value = true
  if (state.filters?.appointment_type) {
    const appointmentTypeIds = state.filters.appointment_type
    const appointmentTypes = await Promise.all(
      appointmentTypeIds.map(async (appointmentTypeId) => {
        return await api.retrieveOfferingType(appointmentTypeId, {
          expand: ['instructors', 'venue'],
        })
      }),
    )

    appointments.value = appointmentTypes
  }
  else {
    const queryParameters = {
      ...(state.filters && state.filters),
      schedule_type: ['appointment'],
      page: state.pagination?.currentPage || 1,
      page_size: state.pagination?.pageSize || 10,

    }

    const result = await api.listOfferingTypes({
      queryParameters,
      expand: ['instructors', 'appointment_settings.default_venue'],
    })

    const paging = inferPagingFromApiResponse(result)
    const { count, perPage, currentPage } = paging
    const totalPages = count && perPage ? Math.ceil(count / perPage) : 1

    setPagination({
      count,
      perPage: perPage || 10,
      currentPage: currentPage || 1,
      totalPages,
    })

    isMultipleAppointmentPages.value = totalPages > 1

    appointments.value = result?.results
  }
  isLoading.value = false
}

dataQueryStore.bus.on('query-updated', fetchContent)

const filters = computed(() => {
  return [
    {
      label: t('Instructor'),
      filterKey: 'instructor',
      type: 'checkboxlist',
      name: 'Instructor',
      options: instructorFiltersOptions.value,
    },
    {
      label: t('Appointment'),
      filterKey: 'appointment_type',
      type: 'checkboxlist',
      name: 'Appointment',
      options: appointmentTypesFilterOptions.value,
    },
    {
      label: t('Venue'),
      filterKey: 'default_appointment_venue',
      type: 'checkboxlist',
      name: 'Venue',
      options: venueFilterOptions.value,
    },
  ]
})

onMounted(() => {
  fetchVenueFilterOptions()
  fetchAppointmentsOfferingTypes()
})
</script>

<template>
  <div class="mx-4">
    <div class="mb-2 border-1 rounded bg-white p-4">
      <DSProvider :data-query-store="dataQueryStore">
        <DataFilterGroup
          :filters="filters"
          :max-filter-controls="maxFilterControlValue"
        />
        <DSLoadingWrapper :loading="isLoading">
          <template v-if="!isLoading">
            <div class="my-2 flex items-end justify-between">
              <span v-if="isMultipleAppointmentPages">
                <DSDataPagerSummary />
              </span>
              <DSDataPager />
            </div>
            <div class="grid-container">
              <template
                v-for="(appointment, index) in appointments"
                :key="index"
              >
                <AppointmentCard
                  v-if="!appointment.is_draft"
                  :appointment="appointment"
                />
              </template>
            </div>
            <div
              class="my-2 flex items-start justify-between"
            >
              <span v-if="isMultipleAppointmentPages">
                <DSDataPagerSummary />
              </span>
              <DSDataPager />
            </div>
          </template>
          <template v-else>
            <div class="my-4">
              <DSAlert
                type="info"
                no-close
                class="w-100% py-4"
              >
                <p class="text-text fw-700">
                  {{ t('No appointments match your criteria') }}
                </p>
              </DSAlert>
            </div>
          </template>
        </DSLoadingWrapper>
      </DSProvider>
    </div>
  </div>
</template>

<i18n>
{
  "en-us": {
    "No appointments match your criteria": "No appointments match your criteria",
    "Instructor": "Instructor",
    "Appointment": "Appointment",
    "Venue": "Venue"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Appointment": "Termin",
  "Instructor": "Trainer",
  "No appointments match your criteria": "Keine Termine entsprechen deiner Kriterien",
  "Venue": "Veranstaltungsort"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Appointment": "Cita",
  "Instructor": "Instructor",
  "No appointments match your criteria": "No hay citas que coincidan con tus criterios",
  "Venue": "Lugar"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Appointment": "Rendez-vous",
  "Instructor": "Instructeur",
  "No appointments match your criteria": "Aucun rendez-vous ne correspond à vos critères",
  "Venue": "Lieu"
}
</i18n>
